import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  OnInit,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { Metadata } from '@app/core/interfaces/metadata';
import { MetaService } from '@app/core/services/meta.service';
import { Image } from '@app/entities/images/interfaces/image';
import { NavItem } from '@app/interfaces/nav-item';
import { NavComponent } from '@app/layout/nav/nav.component';
import {
  ImageSelectors,
  ImageState,
  LoadImage,
  LoadRelatedImages,
} from '@app/store';
import { Navigate } from '@ngxs/router-plugin';
import { Store } from '@ngxs/store';
import { StateReset } from 'ngxs-reset-plugin';
import { filter, map, Observable, tap } from 'rxjs';
import { ContentTypeFilterValues } from '../gallery/content-type-filter/content-type-filter-values';
import { ItemGalleryComponent } from './item-gallery/item-gallery.component';
import { ItemInfoComponent } from './item-info/item-info.component';
import { ItemPreviewComponent } from './item-preview/item-preview.component';
import { ItemTagsComponent } from './item-tags/item-tags.component';
import { PurchaseImageComponent } from './purchase-image/purchase-image.component';

@Component({
  selector: 'app-item-page',
  templateUrl: './item.page.html',
  styleUrls: ['./item.page.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NavComponent,
    ItemPreviewComponent,
    PurchaseImageComponent,
    ItemInfoComponent,
    ItemTagsComponent,
    ItemGalleryComponent,
    AsyncPipe,
  ],
})
export class ItemPage implements OnInit {
  constructor(
    private activatedRoute: ActivatedRoute,
    private store: Store,
    private destroyRef: DestroyRef,
    private metaService: MetaService,
  ) {}

  protected image$: Observable<Image | undefined> = inject(Store).select(
    ImageSelectors.image,
  );

  protected navItems$!: Observable<NavItem[]>;

  ngOnInit(): void {
    this.store.dispatch(new StateReset(ImageState));
    this.setNavigation();
    this.loadImage();
    this.handleImageLoad();
  }

  private setNavigation(): void {
    this.navItems$ = this.image$.pipe(
      map((image: Image | undefined) => {
        if (!image) {
          return [];
        }

        return [
          {
            label: image.categories[0]?.name || 'no category',
            category: image.categories[0]?.name,
          },
          { label: image.name },
        ];
      }),
    );
  }

  private loadImage(): void {
    this.activatedRoute.params
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ id }) => {
        const imageId = +id;
        if (isNaN(imageId)) {
          this.store.dispatch(new Navigate(['/']));
        } else {
          this.store.dispatch(new LoadImage(imageId));
          this.store.dispatch(new LoadRelatedImages(imageId));
        }
      });
  }

  private handleImageLoad(): void {
    this.image$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter(Boolean),
        tap((image: Image) => {
          const imageName = this.getImageName(image);
          const categoryName = this.getCategoryName(image);
          this.updateMeta(imageName, categoryName, image.thumbnail.url);
        }),
      )
      .subscribe();
  }

  private buildTitle(imageName: string, categoryName: string): string {
    return `${imageName} - High-Quality ${categoryName} Videos in RAW & 4K | videosense.net`;
  }

  private buildMetaDescription(
    imageName: string,
    categoryName: string,
  ): string {
    return `Download stunning ${imageName} videos in high resolution. Available in ${ContentTypeFilterValues.ULTRA.toUpperCase()}, ${ContentTypeFilterValues.LARGE.toUpperCase()} and ${ContentTypeFilterValues.MEDIUM.toUpperCase()} formats. Perfect for professional use in websites, blogs, and creative projects. Explore more ${categoryName} videos on imagesense.net.`;
  }

  private buildMetaKeywords(imageName: string, categoryName: string): string {
    return [
      `${imageName} video`,
      `${imageName} video`,
      `high-quality ${imageName} video`,
      `${imageName} RAW video`,
      `${imageName} 4K video`,
      `premium ${imageName} download`,
      `professional ${imageName} video`,
      `${categoryName} videography`,
      `${imageName} stock video`,
      `${imageName} stock video`,
      `${categoryName} videos`,
      `videosense.net`,
    ].join(', ');
  }

  private getImageName(image: Image): string {
    return image.name;
  }

  private getCategoryName(image: Image): string {
    if (!image.categories.length) {
      return '';
    }

    const category = image.categories[0];

    return category.name;
  }

  private updateMeta(
    imageName: string,
    categoryName: string,
    imageUrl: string,
  ): void {
    const metadata: Metadata = {
      title: this.buildTitle(imageName, categoryName),
      description: this.buildMetaDescription(
        imageName,
        categoryName.toLowerCase(),
      ),
      keywords: this.buildMetaKeywords(imageName, categoryName.toLowerCase()),
      imageUrl,
    };
    this.metaService.setMetadata(metadata);
  }
}
